import { UserCorrectiveProgram } from "../../../models/interfaces/corrective-exercises/user-corrective-program";
import FirestoreCondition from "../../../models/interfaces/firestore-condition";
import FirestoreService from "../firestore-service";
const EXTRA_ID = "EXTRA_100_100"; // this is the id from FMS CMS
const COLLECTION_NAME = "userCorrectiveExercises";

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------

/**
 * Add a new user corrective exercises to the userCorrectiveExercises collection
 * @param {UserCorrectiveProgram} program - The corrective exercise that is being added to the collection
 * @param {firebase.User} currentUser - The user that is logged into the application
 * @returns {Promise<UserCorrectiveProgram>} A promise to the newly added corrective exercise
 */
const add = async (
    program: UserCorrectiveProgram,
    currentUser: any | null = null
) => {
    return FirestoreService.add<UserCorrectiveProgram>(
        COLLECTION_NAME,
        program,
        currentUser
    );
};

/**
 * Delete a user collection by the Id
 * @param {string} id - The Id of the user being deleted
 */
const deleteByUser = async (userId: string) => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
    ];

    const records = await FirestoreService.getBy(COLLECTION_NAME, conditions);

    records.forEach((record) => {
        FirestoreService.deleteById(COLLECTION_NAME, record.id!);
    });
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<UserCorrectiveProgram>} A promise for the UserCorrectiveProgram we are retrieving
 */
const get = async (id: string) => {
    return FirestoreService.get<UserCorrectiveProgram>(COLLECTION_NAME, id);
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<UserCorrectiveProgram>} A promise for the UserCorrectiveProgram we are retrieving
 */
const getCurrentByUserId = async (userId: string) => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
        {
            field: "isCurrent",
            operator: "==",
            value: true,
        },
        {
            field: "routineId",
            operator: "!=",
            value: EXTRA_ID,
        },
    ];

    return FirestoreService.getBy<UserCorrectiveProgram>(
        COLLECTION_NAME,
        conditions,
        [
            {
                field: "routineId",
                direction: "desc",
            },
            {
                field: "created",
                direction: "desc",
            },
        ],
        1
    );
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<User>} A promise for the user we are retrieving
 */
const getCurrentExtraByUserId = async (userId: string) => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
        {
            field: "isCurrent",
            operator: "==",
            value: true,
        },
        {
            field: "routineId",
            operator: "==",
            value: EXTRA_ID,
        },
    ];

    return FirestoreService.getBy<UserCorrectiveProgram>(
        COLLECTION_NAME,
        conditions,
        [
            {
                field: "created",
                direction: "desc",
            },
        ],
        1
    );
};

const getAllCurrentByUserId = async (userId: string) => {
    const conditions: FirestoreCondition[] = [
        {
            field: "userId",
            operator: "==",
            value: userId,
        },
        {
            field: "isCurrent",
            operator: "==",
            value: true,
        },
    ];

    return FirestoreService.getBy<UserCorrectiveProgram>(
        COLLECTION_NAME,
        conditions,
        [
            {
                field: "created",
                direction: "desc",
            },
        ],
    );
};

/**
 * Update the user corrective exercise
 * @param {UserCorrectiveProgram} userCorrectiveExercise - The user corrective exercise that is being updated
 * @param {firebase.User} currentUser - The user that is logged into the application
 * @returns {Promise<UserCorrectiveProgram>} A promise for the collection of user corrective exercises
 */
const update = async (
    userCorrectiveExercise: UserCorrectiveProgram,
    currentUser: any | null = null
) => {
    return FirestoreService.update<UserCorrectiveProgram>(
        COLLECTION_NAME,
        userCorrectiveExercise,
        currentUser
    );
};

/**
 * Update the user corrective exercise
 * @param {UserCorrectiveProgram} userCorrectiveProgram - The user corrective exercise that is being updated
 * @param {firebase.User} currentUser - The user that is logged into the application
 * @returns {Promise<UserCorrectiveProgram>} A promise for the collection of user corrective exercises
 */
const updateById = async (
    userCorrectiveProgram: UserCorrectiveProgram,
    currentUserId: string | null = null
) => {
    return FirestoreService.updateById<UserCorrectiveProgram>(
        COLLECTION_NAME,
        userCorrectiveProgram,
        currentUserId
    );
};

// #endregion Service Methods

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const UserCorrectiveProgramsService = {
    add,
    deleteByUser,
    get,
    getCurrentByUserId,
    getCurrentExtraByUserId,
    getAllCurrentByUserId,
    update,
    updateById,
};

export default UserCorrectiveProgramsService;

// #endregion Exports
